import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Radio,
  Typography,
  Input,
  Space,
  Badge,
  Empty,
  Skeleton,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import PaginationTable from "../../../shared/PaginationTable";
import { useQuery } from "react-apollo";
import GET_FILTERED_HASHED_EMAIL_LIST_WITH_COUNT from "../../../../core/GraphQl/Queries/GET_FILTERED_HASHED_EMAIL_LIST_WITH_COUNT";
import moment from "moment";

const { Title, Text } = Typography;

// Helper function to map DSP names based on providersVisible permission
const getDSPDisplayName = (dspName, providersVisible) => {
  if (providersVisible) {
    return dspName; // Show actual DSP name
  }

  // Map to tier names when providersVisible is false
  switch (dspName?.toLowerCase()) {
    case 'liquidm':
      return 'Tier 1';
    case 'nexxen':
      return 'Tier 2';
    default:
      return dspName; // Return original if not mapped
  }
};

const SourceAudienceSelectionModal = ({
  visible,
  onClose,
  onSelect,
  currentOrg,
  onActivatedCountChange,
  providersVisible,
}) => {
  const [selectedAudienceId, setSelectedAudienceId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: hashedEmailLists,
    loading: loadingHashedEmailLists,
  } = useQuery(GET_FILTERED_HASHED_EMAIL_LIST_WITH_COUNT, {
    variables: {
      orgId: currentOrg?.id,
      searchTerm: searchTerm || null
    },
    fetchPolicy: 'cache-and-network',
    skip: !visible,
  });

  const hashedEmailListExtracted = useMemo(() => {
    if (!Array.isArray(hashedEmailLists) &&
      hashedEmailLists &&
      hashedEmailLists.getFilteredHashedEmailListWithCount &&
      hashedEmailLists.getFilteredHashedEmailListWithCount.hashedEmailList
    ) {
      return hashedEmailLists.getFilteredHashedEmailListWithCount.hashedEmailList;
    }
    return [];
  }, [hashedEmailLists]);

  const activatedAudiences = useMemo(() => {
    return hashedEmailListExtracted.filter((audience) => {
      const dspName = audience.dspSegmentIds && audience.dspSegmentIds.length
        ? audience.dspSegmentIds[0].dsp
        : audience.dsp;
      const isLiquidM = dspName && dspName.toUpperCase() === "LIQUIDM";

      return !isLiquidM;
    });
  }, [hashedEmailListExtracted]);


  useEffect(() => {
    if (onActivatedCountChange && !loadingHashedEmailLists && visible && hashedEmailLists) {
      onActivatedCountChange(activatedAudiences.length);
    }
  }, [activatedAudiences.length, loadingHashedEmailLists, onActivatedCountChange, visible, hashedEmailLists]);

  const handleSelectAudience = () => {
    if (!selectedAudienceId) {
      return message.warning("Please select a source audience.");
    }

    const selectedAudience = activatedAudiences.find(
      (aud) => aud.id === selectedAudienceId
    );

    if (!selectedAudience) {
      return message.error("Selected audience not found.");
    }

    if (!selectedAudience.csvFileName) {
      return message.error(
        "Selected audience does not have a CSV file. Please select a different audience."
      );
    }

    onSelect(selectedAudience);
    onClose();
    setSelectedAudienceId(null);
    setSearchTerm("");
  };

  const handleCancel = () => {
    onClose();
    setSelectedAudienceId(null);
    setSearchTerm("");
  };

  const columns = [
    {
      title: "Select",
      dataIndex: "select",
      key: "select",
      width: 80,
      render: (_, record) => (
        <Radio
          checked={selectedAudienceId === record.id}
          onChange={() => {
            if (record.csvFileName) {
              setSelectedAudienceId(record.id);
              onSelect(record);
            }
          }}
          aria-label={`Select ${record.name}`}
        />
      ),
    },
    {
      title: "Audience Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {!record.csvFileName && (
            <div>
              <Badge status="warning" />
              <Text type="warning" style={{ fontSize: "12px" }}>
                No CSV file available
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Data Type",
      dataIndex: "typeOfFirstPartyData",
      key: "type",
      render: (type) => {
        const typeLabels = {
          emails: "Email Addresses",
          phones: "Phone Numbers",
          lookalike: "Lookalike Audiences",
        };
        return typeLabels[type] || type || "Email Addresses";
      },
    },
    {
      title: "Hashed Count",
      dataIndex: "count",
      key: "count",
      render: (count) => (count || 0).toLocaleString(),
    },
    {
      title: "Ingested Devices",
      dataIndex: "deviceCount",
      key: "deviceCount",
      render: (deviceCount) => (deviceCount || 0).toLocaleString(),
    },
    {
      title: "DSP",
      dataIndex: "dsp",
      key: "dsp",
      render: (_, record) => {
        const dspName = record.dspSegmentIds && record.dspSegmentIds.length
          ? record.dspSegmentIds[0].dsp
          : record.dsp;
        const displayName = getDSPDisplayName(dspName, providersVisible);
        return displayName && typeof displayName === "string" ? displayName.toUpperCase() : "N/A";
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? moment(date).format("MM/DD/YYYY") : "-"),
    },
    {
      title: "Segment Information",
      dataIndex: "segmentInformation",
      key: "segmentInformation",
      width: '20%',
      render: (_, record) => {
        const segmentInfo =
          record.dspSegmentIds && record.dspSegmentIds.length
            ? record.dspSegmentIds[0]
            : { segmentId: "", segmentName: "" };
        
        const hasSegmentId = segmentInfo.segmentId && segmentInfo.segmentId.trim() !== "";
        const hasSegmentName = segmentInfo.segmentName && segmentInfo.segmentName.trim() !== "";
        
        if (!hasSegmentId && !hasSegmentName) {
          return "";
        }
        
        return (
          <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
            <div style={{ wordBreak: "break-all", whiteSpace: "normal" }}>
              ID: {segmentInfo.segmentId || "N/A"}
            </div>
            <div>Name: {segmentInfo.segmentName || "N/A"}</div>
          </div>
        );
      },
    },
  ];


  return (
    <Modal
      title="Select Source Audience"
      visible={visible}
      onOk={handleSelectAudience}
      onCancel={handleCancel}
      okText="Select"
      cancelText="Cancel"
      width={1400}
      style={{ top: 20 }}
      bodyStyle={{ 
        display: 'flex', 
        flexDirection: 'column', 
        maxHeight: '80vh',
        padding: 0
      }}
      okButtonProps={{
        disabled: !selectedAudienceId,
      }}
    >
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        overflow: 'hidden'
      }}>
        <div style={{ 
          flex: '0 0 auto', 
          padding: '24px 24px 16px 24px'
        }}>
          <Title level={5}>Choose an Activated Audience</Title>
          <Text type="secondary" style={{ display: "block", marginBottom: "16px" }}>
            Select an already activated audience to use as the source for your lookalike audience.
            Only activated audiences with available CSV files are shown (excluding phone numbers, lookalike audiences, and LIQUIDM DSP).
          </Text>

          <Space style={{ marginBottom: "16px", width: "100%" }} direction="vertical">
            <Input
              placeholder="Search by audience name"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              aria-label="Search audiences"
            />
          </Space>
        </div>

        <div style={{ 
          flex: '1 1 auto', 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: '24px',
          paddingRight: '24px'
        }}>
          {loadingHashedEmailLists ? (
            <div style={{ padding: "20px" }}>
              <Skeleton active title={false} paragraph={{ rows: 5 }} />
            </div>
          ) : activatedAudiences.length > 0 ? (
            <>
              {activatedAudiences.some((aud) => !aud.csvFileName) && (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    backgroundColor: "#fffbe6",
                    border: "1px solid #ffe58f",
                    borderRadius: "4px",
                    flex: '0 0 auto'
                  }}
                >
                  <Text type="warning" style={{ fontSize: "13px" }}>
                    Some audiences don't have CSV files and cannot be selected. Only audiences
                    with CSV files can be used as lookalike sources.
                  </Text>
                </div>
              )}

              {selectedAudienceId && (
                <div style={{ marginBottom: "12px", flex: '0 0 auto' }}>
                  <Badge status="success" />
                  <Text style={{ fontSize: "13px", color: "#52c41a" }}>
                    1 audience selected
                  </Text>
                </div>
              )}

              <div style={{ flex: '1 1 auto', overflow: 'hidden' }}>
                <PaginationTable
                  columns={columns}
                  data={activatedAudiences}
                  total={activatedAudiences.length}
                  bordered={true}
                  size="small"
                  pageSize={10}
                  scroll={{ y: 'calc(80vh - 400px)', x: 1200 }}
                  onRow={(record) => ({
                    onClick: () => {
                      if (record.csvFileName) {
                        setSelectedAudienceId(record.id);
                        onSelect(record);
                      }
                    },
                    style: {
                      cursor: record.csvFileName ? "pointer" : "not-allowed",
                      backgroundColor:
                        selectedAudienceId === record.id ? "#e6f7ff" : undefined,
                      opacity: record.csvFileName ? 1 : 0.5,
                    },
                  })}

                />
              </div>
            </>
          ) : (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <Empty
                description={
                  searchTerm
                    ? `No activated audiences found matching "${searchTerm}"`
                    : "No activated audiences available."
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );

};

export default SourceAudienceSelectionModal;
